<template>
  <div class="channel-page" :class="{ loading: loading }">
    <!-- Not Found -->
    <div v-if="notFound" class="channel-not-found">
      <div class="nf-card">
        <svg v-if="isPrivate" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="1.2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        <svg v-else width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="1.2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
        <h1>{{ isPrivate ? t('channel.channelPrivate') : t('channel.channelNotFound') }}</h1>
        <p>{{ isPrivate ? t('channel.channelPrivateDesc') : t('channel.channelNotFoundDesc') }}</p>
        <a href="https://virtualtoureasy.com" class="nf-link">{{ t('channel.backHome') }} →</a>
      </div>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="channel-loading">
      <div class="loader"></div>
      <span>{{ t('common.loading') }}</span>
    </div>

    <!-- Channel Content -->
    <div v-else-if="channel" class="channel-content">
      <!-- Header -->
      <header class="channel-header">
        <!-- Banner image -->
        <div
          v-if="channel.banner"
          class="channel-banner"
          :style="{ backgroundImage: `url(${channel.banner})` }"
        ></div>
        <div class="header-inner" :class="{ 'has-banner': channel.banner }">
          <div class="channel-identity">
            <div class="channel-logo" v-if="channel.logo">
              <img :src="channel.logo" :alt="channel.name" />
            </div>
            <div class="channel-logo placeholder" v-else>
              <span>{{ channel.name?.charAt(0)?.toUpperCase() || '?' }}</span>
            </div>
            <div class="channel-info">
              <h1 class="channel-name">{{ channel.name }}</h1>
              <p v-if="channel.description" class="channel-description">{{ channel.description }}</p>
              <div class="channel-meta">
                <span class="meta-item">{{ channel.tour_count }} {{ t('channel.tours').toLowerCase() }}</span>
                <span class="meta-dot">·</span>
                <span class="meta-item">{{ formatViews(channel.total_views) }} {{ t('channel.views') }}</span>
                <template v-if="channel.website">
                  <span class="meta-dot">·</span>
                  <a :href="channel.website" target="_blank" rel="noopener" class="meta-link">{{ t('channel.website') }}</a>
                </template>
              </div>
            </div>
          </div>
          <div class="header-actions">
            <!-- Language Selector -->
            <div class="lang-select-wrap">
              <select v-model="currentLang" @change="changeLang" class="lang-select" :title="t('channel.language')">
                <option v-for="lang in languages" :key="lang.code" :value="lang.code">
                  {{ lang.flag }} {{ lang.nativeName }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </header>

      <!-- Search & Sort Bar -->
      <div class="toolbar">
        <div class="toolbar-inner">
          <div class="search-box">
            <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="t('channel.search')"
              @input="debouncedSearch"
              class="search-input"
            />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''; fetchTours()">×</button>
          </div>
          <div class="sort-pills">
            <button
              v-for="s in sortOptions"
              :key="s.value"
              class="sort-pill"
              :class="{ active: sort === s.value }"
              @click="sort = s.value; fetchTours()"
            >{{ s.label }}</button>
          </div>
        </div>
      </div>

      <!-- Tags -->
      <div class="tags-bar" v-if="allTags.length > 0">
        <div class="tags-inner">
          <button
            class="tag-chip"
            :class="{ active: !activeTag }"
            @click="activeTag = ''; fetchTours()"
          >{{ t('channel.allTags') }}</button>
          <button
            v-for="tag in allTags"
            :key="tag"
            class="tag-chip"
            :class="{ active: activeTag === tag }"
            @click="activeTag = tag; fetchTours()"
          >{{ tag }}</button>
        </div>
      </div>

      <!-- Tour Grid -->
      <main class="tour-grid-wrap">
        <div class="tour-grid" v-if="tours.length > 0">
          <a
            v-for="tour in tours"
            :key="tour.uuid"
            :href="viewerUrl + '/?v=' + tour.uuid"
            class="tour-card"
            @click.prevent="goToTour(tour.uuid)"
          >
            <div class="card-thumb">
              <img v-if="tour.thumbnail" :src="tour.thumbnail" :alt="tour.title" loading="lazy" />
              <div v-else class="thumb-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#475569" stroke-width="1.2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></svg>
              </div>
              <div class="card-views">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/></svg>
                {{ formatViews(tour.view_count) }}
              </div>
              <div class="card-scenes">{{ tour.scene_count }} {{ t('channel.scenes') }}</div>
            </div>
            <div class="card-body">
              <h3 class="card-title">{{ tour.title || 'Untitled Tour' }}</h3>
              <p class="card-desc" v-if="tour.description">{{ truncate(tour.description, 80) }}</p>
              <div class="card-meta">
                <span class="card-location" v-if="tour.location">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                  {{ tour.location.name }}
                </span>
                <div class="card-tags" v-if="tour.tags?.length">
                  <span v-for="tag in tour.tags.slice(0, 3)" :key="tag" class="mini-tag">{{ tag }}</span>
                </div>
              </div>
            </div>
          </a>
        </div>

        <!-- Empty States -->
        <div v-else-if="!toursLoading" class="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#475569" stroke-width="1.2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></svg>
          <p>{{ searchQuery || activeTag ? t('channel.noResults') : t('channel.noTours') }}</p>
        </div>

        <!-- Loading tours -->
        <div v-if="toursLoading" class="tours-loading">
          <div class="loader small"></div>
        </div>

        <!-- Pagination -->
        <div class="pagination" v-if="totalPages > 1">
          <button class="page-btn" :disabled="page <= 1" @click="page--; fetchTours()">←</button>
          <span class="page-info">{{ page }} / {{ totalPages }}</span>
          <button class="page-btn" :disabled="page >= totalPages" @click="page++; fetchTours()">→</button>
        </div>
      </main>

      <!-- Footer -->
      <footer class="channel-footer">
        <div class="footer-inner">
          <a href="https://virtualtoureasy.com" target="_blank" class="powered-by">
            {{ t('channel.poweredBy') }}
            <strong>VirtualTourEasy</strong>
          </a>
          <div class="footer-handle" v-if="handle">
            <span class="handle-url">{{ viewerHost }}/c/{{ handle }}</span>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { availableLanguages, setLocale, getLocale, type SupportedLocale } from '../i18n'

const { t } = useI18n()

const props = defineProps<{
  handle: string
  apiUrl: string
  injectedBranding?: any | null // branding from custom domain resolution (overrides channel branding)
}>()

const emit = defineEmits<{
  (e: 'open-tour', uuid: string): void
}>()

// State
const loading = ref(true)
const notFound = ref(false)
const isPrivate = ref(false)
const channel = ref<any>(null)
const tours = ref<any[]>([])
const toursLoading = ref(false)
const searchQuery = ref('')
const sort = ref('newest')
const activeTag = ref('')
const page = ref(1)
const totalPages = ref(1)
const allTags = ref<string[]>([])
const currentLang = ref(getLocale())
const handle = computed(() => props.handle)

const languages = availableLanguages
const viewerUrl = computed(() => window.location.origin)
const viewerHost = computed(() => window.location.host)

const sortOptions = computed(() => [
  { value: 'newest', label: t('channel.sortNewest') },
  { value: 'popular', label: t('channel.sortPopular') },
  { value: 'title', label: t('channel.sortTitle') },
])

// Debounced search
let searchTimeout: number | null = null
function debouncedSearch() {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = window.setTimeout(() => {
    page.value = 1
    fetchTours()
  }, 400)
}

function changeLang() {
  setLocale(currentLang.value as SupportedLocale)
}

function formatViews(n: number): string {
  if (!n) return '0'
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(n)
}

function truncate(str: string, len: number): string {
  return str.length > len ? str.slice(0, len) + '…' : str
}

function goToTour(uuid: string) {
  emit('open-tour', uuid)
  window.location.href = viewerUrl.value + '/?v=' + uuid
}

/**
 * Apply org branding to the channel page.
 * Sets CSS custom properties for colors, loads Google Font, injects custom CSS,
 * updates favicon, and hides powered-by badge if disabled.
 * Works for both standard branding (channel.branding) and custom-domain injection.
 */
function applyBranding(b: any, ch: any) {
  const root = document.documentElement

  // ── Colors → CSS custom properties ────────────────────────────────────────
  const colors = b.colors
  if (colors) {
    if (colors.primary) {
      root.style.setProperty('--brand-primary', colors.primary)
      // Also set RGB triplet for rgba() usage
      const rgb = hexToRgb(colors.primary)
      if (rgb) root.style.setProperty('--brand-primary-rgb', rgb)
    }
    if (colors.secondary)  root.style.setProperty('--brand-secondary', colors.secondary)
    if (colors.accent)     root.style.setProperty('--brand-accent',    colors.accent)
    if (colors.background) root.style.setProperty('--brand-bg',        colors.background)
    if (colors.text)       root.style.setProperty('--brand-text',      colors.text)
  }

  // ── Custom font ────────────────────────────────────────────────────────────
  const font = b.font || b.viewer_font
  if (font) {
    // Inject Google Fonts link if not already present
    const fontId = `brand-font-${font.replace(/\s+/g, '-')}`
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link')
      link.id = fontId
      link.rel = 'stylesheet'
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@400;500;600;700&display=swap`
      document.head.appendChild(link)
    }
    root.style.setProperty('--brand-font', `'${font}', sans-serif`)
  }

  // ── Favicon ────────────────────────────────────────────────────────────────
  const faviconUrl = b.favicon_url || b.favicon
  if (faviconUrl) {
    let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link) }
    link.href = faviconUrl
  }

  // ── Company name override ──────────────────────────────────────────────────
  const companyName = b.company_name
  if (companyName && ch) ch.name = companyName

  // ── Custom CSS ────────────────────────────────────────────────────────────
  const css = b.custom_css
  if (css) {
    const existing = document.getElementById('brand-custom-css')
    if (existing) existing.remove()
    const style = document.createElement('style')
    style.id = 'brand-custom-css'
    style.textContent = css
    document.head.appendChild(style)
  }

  // ── Powered-by badge ───────────────────────────────────────────────────────
  const poweredByEnabled = b.powered_by_enabled !== false && b.powered_by_enabled !== 0
  if (!poweredByEnabled) {
    document.querySelectorAll('.powered-by, .vte-powered, [data-powered-by]')
      .forEach(el => (el as HTMLElement).style.display = 'none')
  }

  // ── Page title ─────────────────────────────────────────────────────────────
  if (ch?.name) document.title = ch.name
}

/** Convert #rrggbb or #rgb to "r,g,b" string for CSS rgba() */
function hexToRgb(hex: string): string | null {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const n = parseInt(full, 16)
  if (isNaN(n)) return null
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`
}

async function fetchChannel() {
  loading.value = true
  try {
    const res = await fetch(`${props.apiUrl}/api/channels/${props.handle}`)
    if (res.status === 403) {
      isPrivate.value = true
      notFound.value = true
      return
    }
    if (!res.ok) {
      notFound.value = true
      return
    }
    channel.value = await res.json()

    // Apply branding to the channel page
    // Priority: injectedBranding (custom domain) > channel.branding (standard)
    const b = props.injectedBranding || channel.value.branding
    if (b) {
      applyBranding(b, channel.value)
    }

    await fetchTours()
  } catch (e) {
    console.error('Failed to load channel', e)
    notFound.value = true
  } finally {
    loading.value = false
  }
}

async function fetchTours() {
  toursLoading.value = true
  try {
    const params = new URLSearchParams({
      page: String(page.value),
      limit: '20',
      sort: sort.value,
    })
    if (searchQuery.value) params.set('q', searchQuery.value)
    if (activeTag.value) params.set('tag', activeTag.value)

    const res = await fetch(`${props.apiUrl}/api/channels/${props.handle}/tours?${params}`)
    if (!res.ok) throw new Error('Failed')
    const data = await res.json()
    tours.value = data.tours || []
    totalPages.value = data.total_pages || 1
    allTags.value = data.tags || []
  } catch (e) {
    console.error('Failed to load tours', e)
  } finally {
    toursLoading.value = false
  }
}

onMounted(() => {
  fetchChannel()
})
</script>

<style scoped>
/* ── Branding CSS variables ───────────────────────────────────────────────────
 * Defaults match the original dark theme.
 * applyBranding() overrides these on :root via style.setProperty() when
 * org branding is loaded, so all elements below pick up the custom palette.
 * ─────────────────────────────────────────────────────────────────────────── */
:root {
  --brand-primary:   #3b82f6;
  --brand-secondary: #64748b;
  --brand-accent:    #8b5cf6;
  --brand-bg:        #0f172a;
  --brand-text:      #e2e8f0;
  --brand-font:      system-ui, -apple-system, sans-serif;
}

.channel-page {
  min-height: 100vh;
  background: var(--brand-bg, #0f172a);
  color: var(--brand-text, #e2e8f0);
  font-family: var(--brand-font, system-ui, -apple-system, sans-serif);
  display: flex;
  flex-direction: column;
}

/* Loading / Not Found */
.channel-loading, .channel-not-found {
  flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 16px;
  min-height: 100vh;
}
.loader {
  width: 32px; height: 32px; border: 3px solid rgba(59,130,246,0.2);
  border-top-color: var(--brand-primary, #3b82f6); border-radius: 50%; animation: spin 0.8s linear infinite;
}
.loader.small { width: 24px; height: 24px; border-width: 2px; }
@keyframes spin { to { transform: rotate(360deg); } }
.nf-card { text-align: center; max-width: 400px; padding: 48px 32px; }
.nf-card h1 { font-size: 20px; margin: 16px 0 8px; color: #f1f5f9; }
.nf-card p { color: #94a3b8; font-size: 14px; margin-bottom: 24px; }
.nf-link {
  display: inline-block; padding: 10px 24px;
  background: var(--brand-primary, #3b82f6); color: white; text-decoration: none;
  border-radius: 8px; font-weight: 500; font-size: 14px;
}
.nf-link:hover { background: #2563eb; }

/* Channel Content */
.channel-content { display: flex; flex-direction: column; min-height: 100vh; }

/* Header */
.channel-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-bottom: 1px solid rgba(148,163,184,0.1);
}
.channel-banner {
  width: 100%; height: 200px;
  background-size: cover; background-position: center;
  background-repeat: no-repeat;
}
.header-inner {
  max-width: 1200px; margin: 0 auto; padding: 24px 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
}
.header-inner.has-banner {
  padding-top: 20px;
  margin-top: -28px;
  position: relative;
}
.channel-identity { display: flex; align-items: flex-start; gap: 16px; }
.channel-logo {
  width: 72px; height: 72px; border-radius: 14px; overflow: hidden; flex-shrink: 0;
  background: #1e293b; border: 2px solid rgba(148,163,184,0.2);
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
}
.header-inner.has-banner .channel-logo {
  width: 80px; height: 80px;
  border: 3px solid rgba(255,255,255,0.15);
}
.channel-logo img { width: 100%; height: 100%; object-fit: cover; }
.channel-logo.placeholder {
  display: flex; align-items: center; justify-content: center;
  font-size: 28px; font-weight: 700; color: var(--brand-primary, #3b82f6);
  background: linear-gradient(135deg, #1e3a5f, #1e293b);
}
.channel-name { font-size: 22px; font-weight: 700; color: #f1f5f9; margin: 0 0 4px; }
.channel-description {
  font-size: 13px; color: #94a3b8; margin: 0 0 6px;
  max-width: 500px; line-height: 1.5;
}
.channel-meta { display: flex; gap: 8px; align-items: center; }
.meta-item { font-size: 13px; color: #94a3b8; }
.meta-dot { color: #475569; }
.meta-link { font-size: 13px; color: var(--brand-primary, #60a5fa); text-decoration: none; }
.meta-link:hover { text-decoration: underline; }

.lang-select-wrap { position: relative; }
.lang-select {
  background: rgba(255,255,255,0.06); border: 1px solid rgba(148,163,184,0.15);
  color: #e2e8f0; padding: 6px 12px; border-radius: 6px; font-size: 13px;
  cursor: pointer; outline: none;
}
.lang-select option { background: #1e293b; color: #e2e8f0; }

/* Toolbar */
.toolbar {
  background: rgba(30,41,59,0.5); border-bottom: 1px solid rgba(148,163,184,0.08);
  padding: 12px 0; position: sticky; top: 0; z-index: 10;
  backdrop-filter: blur(12px);
}
.toolbar-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: flex; align-items: center; gap: 16px;
}
.search-box {
  flex: 1; max-width: 400px; position: relative; display: flex; align-items: center;
}
.search-icon { position: absolute; left: 12px; color: #64748b; pointer-events: none; }
.search-input {
  width: 100%; padding: 8px 36px 8px 40px; border-radius: 8px;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(148,163,184,0.12);
  color: #e2e8f0; font-size: 14px; outline: none;
  transition: border-color 0.2s;
}
.search-input:focus { border-color: var(--brand-primary, #3b82f6); }
.search-input::placeholder { color: #64748b; }
.search-clear {
  position: absolute; right: 8px; background: none; border: none;
  color: #64748b; font-size: 18px; cursor: pointer; padding: 2px 6px;
}
.search-clear:hover { color: #e2e8f0; }

.sort-pills { display: flex; gap: 6px; }
.sort-pill {
  padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: 500;
  background: transparent; border: 1px solid rgba(148,163,184,0.12);
  color: #94a3b8; cursor: pointer; transition: all 0.15s; white-space: nowrap;
}
.sort-pill:hover { background: rgba(255,255,255,0.06); color: #e2e8f0; }
.sort-pill.active { background: var(--brand-primary, #3b82f6); border-color: var(--brand-primary, #3b82f6); color: white; }

/* Tags */
.tags-bar {
  border-bottom: 1px solid rgba(148,163,184,0.08);
  padding: 8px 0;
}
.tags-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: flex; gap: 6px; overflow-x: auto;
  scrollbar-width: none;
}
.tags-inner::-webkit-scrollbar { display: none; }
.tag-chip {
  padding: 4px 14px; border-radius: 16px; font-size: 12px; font-weight: 500;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(148,163,184,0.1);
  color: #94a3b8; cursor: pointer; white-space: nowrap; transition: all 0.15s;
}
.tag-chip:hover { background: rgba(255,255,255,0.08); color: #e2e8f0; }
.tag-chip.active { background: rgba(var(--brand-primary-rgb, 59,130,246),0.15); border-color: rgba(var(--brand-primary-rgb, 59,130,246),0.3); color: var(--brand-primary, #60a5fa); }

/* Tour Grid */
.tour-grid-wrap { flex: 1; padding: 24px 0; }
.tour-grid {
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.tour-card {
  background: #1e293b; border-radius: 12px; overflow: hidden;
  border: 1px solid rgba(148,163,184,0.08);
  transition: all 0.2s; cursor: pointer; text-decoration: none; color: inherit;
}
.tour-card:hover {
  border-color: rgba(var(--brand-primary-rgb, 59,130,246),0.3);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.3);
}

.card-thumb {
  position: relative; width: 100%; padding-top: 56.25%; /* 16:9 */
  background: #0f172a; overflow: hidden;
}
.card-thumb img {
  position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
}
.thumb-placeholder {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #0f172a, #1e293b);
}
.card-views {
  position: absolute; bottom: 8px; right: 8px;
  background: rgba(0,0,0,0.7); backdrop-filter: blur(4px);
  padding: 3px 8px; border-radius: 4px;
  font-size: 11px; color: #e2e8f0;
  display: flex; align-items: center; gap: 4px;
}
.card-scenes {
  position: absolute; bottom: 8px; left: 8px;
  background: rgba(0,0,0,0.7); backdrop-filter: blur(4px);
  padding: 3px 8px; border-radius: 4px;
  font-size: 11px; color: #e2e8f0;
}

.card-body { padding: 12px 14px; }
.card-title {
  font-size: 15px; font-weight: 600; color: #f1f5f9; margin: 0 0 4px;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.card-desc {
  font-size: 12px; color: #94a3b8; margin: 0 0 8px; line-height: 1.4;
}
.card-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.card-location {
  display: flex; align-items: center; gap: 3px;
  font-size: 11px; color: #64748b;
}
.card-tags { display: flex; gap: 4px; }
.mini-tag {
  font-size: 10px; padding: 1px 8px; border-radius: 10px;
  background: rgba(var(--brand-primary-rgb, 59,130,246),0.1); color: var(--brand-primary, #60a5fa);
}

/* Empty */
.empty-state {
  max-width: 1200px; margin: 0 auto; padding: 64px 24px;
  text-align: center; color: #64748b;
}
.empty-state p { margin-top: 12px; font-size: 15px; }

/* Tours loading */
.tours-loading { padding: 32px; text-align: center; }

/* Pagination */
.pagination {
  max-width: 1200px; margin: 24px auto 0; padding: 0 24px;
  display: flex; align-items: center; justify-content: center; gap: 16px;
}
.page-btn {
  padding: 8px 16px; border-radius: 6px; font-size: 14px;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(148,163,184,0.12);
  color: #e2e8f0; cursor: pointer;
}
.page-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.page-btn:not(:disabled):hover { background: rgba(255,255,255,0.1); }
.page-info { font-size: 14px; color: #94a3b8; }

/* Footer */
.channel-footer {
  margin-top: auto;
  border-top: 1px solid rgba(148,163,184,0.08);
  padding: 16px 0;
}
.footer-inner {
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: flex; align-items: center; justify-content: space-between;
}
.powered-by {
  font-size: 12px; color: #64748b; text-decoration: none;
}
.powered-by strong { color: var(--brand-primary, #3b82f6); }
.powered-by:hover strong { text-decoration: underline; }
.footer-handle { }
.handle-url {
  font-size: 12px; color: #475569;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

/* Responsive */
@media (max-width: 640px) {
  .header-inner { flex-direction: column; align-items: flex-start; }
  .toolbar-inner { flex-direction: column; align-items: stretch; }
  .search-box { max-width: none; }
  .sort-pills { justify-content: center; }
  .tour-grid { grid-template-columns: 1fr; }
  .channel-name { font-size: 18px; }
  .footer-inner { flex-direction: column; gap: 8px; text-align: center; }
}
</style>
